import {ChangeDetectionStrategy, Component, computed, inject, input} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ReactiveFormsModule} from '@angular/forms';
import {InputDropdownGroupComponent, InputNumberGroupComponent} from '@brunata-angular/ui';
import {
    FeatureFlagService,
    formatDate,
    TRANSLATE,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {DateTime} from 'luxon';
import {
    maxScheduleDayOfMonth,
    SavedExportWeekday,
    savedExportSchedulePeriodSchema,
    savedExportScheduleUnits,
    savedExportWeekdays,
    scheduleUnitLabelKeys,
    weekdayLabelKeys,
} from '../../saved-exports/saved-export.model';
import {ExportScheduleForm, scheduleFormValue} from '../../shared/models/export-form.model';
import {OptionItem} from '../../shared/ui/option-card/option-card.model';
import {OptionSelectComponent} from '../../shared/ui/option-select/option-select.component';
import {nextMonthlyDeliveries} from './next-deliveries.model';

const weekdayOptions = savedExportWeekdays.map(weekday => ({
    value: weekday,
    labelKey: weekdayLabelKeys[weekday],
}));

const daysBackOption: OptionItem = {
    id: 'DAYS_BACK',
    titleKey: 'savedexport_custom_schedule_period_days_back_title',
    descriptionKey: 'savedexport_custom_schedule_period_days_back_desc',
};

const setDayOption: OptionItem = {
    id: 'DAYS_BACK',
    titleKey: 'savedexport_custom_schedule_period_set_day_title',
    descriptionKey: 'savedexport_custom_schedule_period_set_day_desc',
};

const sentOnFormat: Intl.DateTimeFormatOptions = {day: 'numeric', month: 'short', year: 'numeric'};

const holdsFormat: Intl.DateTimeFormatOptions = {day: 'numeric', month: 'short'};

@Component({
    selector: 'app-custom-schedule',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        TranslatePipe,
        InputDropdownGroupComponent,
        InputNumberGroupComponent,
        OptionSelectComponent,
    ],
    template: `
        <div class="custom-schedule border rounded p-3">
            <div class="form-check">
                <input
                    type="checkbox"
                    class="form-check-input"
                    id="custom-schedule"
                    [formControl]="schedule().controls.customSchedule" />
                <label class="form-check-label fw-semibold" for="custom-schedule">
                    {{ 'savedexport_custom_schedule_label' | translate: 'online' : 'export' }}
                </label>
                <div class="form-text">
                    {{ 'savedexport_custom_schedule_hint' | translate: 'online' : 'export' }}
                </div>
            </div>

            @if (value()?.customSchedule === true) {
                <hr />
                <div class="d-flex flex-wrap align-items-center gap-3">
                    <label class="col-form-label" for="custom-schedule-interval">
                        {{ 'savedexport_custom_schedule_every' | translate: 'online' : 'export' }}
                    </label>
                    <app-input-number-group
                        class="interval-input"
                        [id]="'custom-schedule-interval'"
                        [formControl]="schedule().controls.scheduleInterval" />
                    <app-input-dropdown-group
                        class="unit-select"
                        [formControl]="schedule().controls.scheduleUnit"
                        [data]="unitOptions()" />

                    @if (dayOfMonthShown()) {
                        <label class="col-form-label" for="custom-schedule-day">
                            {{
                                'savedexport_custom_schedule_day_of_month_label'
                                    | translate: 'online' : 'export'
                            }}
                        </label>
                        <app-input-number-group
                            class="interval-input"
                            [id]="'custom-schedule-day'"
                            [formControl]="schedule().controls.scheduleDayOfMonth" />
                    }
                </div>

                @if (periodOffered()) {
                    <div class="mt-3">
                        <div class="form-label fw-semibold">
                            {{
                                'savedexport_custom_schedule_file_holds_label'
                                    | translate: 'online' : 'export'
                            }}
                        </div>
                        <app-option-select
                            name="custom-schedule-period"
                            ariaKey="savedexport_custom_schedule_file_holds_label"
                            [options]="periodOptions()"
                            [selectedId]="period()"
                            (optionSelected)="selectPeriod($event)" />
                    </div>

                    @if (nextDeliveries().length > 0) {
                        <div class="mt-3">
                            <div class="form-label fw-semibold">
                                {{
                                    'savedexport_custom_schedule_next_deliveries_label'
                                        | translate: 'online' : 'export'
                                }}
                            </div>
                            <table class="table table-striped next-deliveries mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">{{ sentOnHeader() }}</th>
                                        <th scope="col">
                                            {{
                                                'savedexport_custom_schedule_next_holds'
                                                    | translate: 'online' : 'export'
                                            }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (delivery of nextDeliveries(); track delivery.sentOn) {
                                        <tr>
                                            <td>{{ delivery.sentOn }}</td>
                                            <td>{{ delivery.holds }}</td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                }

                @if (unit() === 'WEEKS') {
                    <div class="mt-3">
                        <div class="form-label" id="custom-schedule-weekdays">
                            {{
                                'savedexport_custom_schedule_weekdays_label'
                                    | translate: 'online' : 'export'
                            }}
                        </div>
                        <div
                            class="btn-group flex-wrap"
                            role="group"
                            aria-labelledby="custom-schedule-weekdays">
                            @for (option of weekdayOptions; track option.value) {
                                <button
                                    type="button"
                                    class="btn"
                                    [class.btn-primary]="selectedWeekdays().includes(option.value)"
                                    [class.btn-outline-primary]="
                                        !selectedWeekdays().includes(option.value)
                                    "
                                    [attr.aria-pressed]="selectedWeekdays().includes(option.value)"
                                    (click)="toggleWeekday(option.value)">
                                    {{ option.labelKey | translate: 'online' : 'export' }}
                                </button>
                            }
                        </div>
                        @if (selectedWeekdays().length === 0) {
                            <div class="invalid-feedback d-block">
                                {{
                                    'savedexport_custom_schedule_weekdays_required'
                                        | translate: 'online' : 'export'
                                }}
                            </div>
                        }
                    </div>
                }

                @if (!periodOffered()) {
                    <div class="schedule-summary mt-3 ps-3 py-2">
                        @if (summaryComplete()) {
                            <div class="fw-semibold">
                                {{
                                    summaryKey()
                                        | translate: 'online' : 'export' : summaryArguments()
                                }}
                            </div>
                        }
                        <div class="text-secondary">
                            {{
                                'savedexport_custom_schedule_utc_note'
                                    | translate: 'online' : 'export'
                            }}
                            @if (unit() === 'MONTHS') {
                                {{
                                    'savedexport_custom_schedule_day_of_month_note'
                                        | translate: 'online' : 'export'
                                }}
                            }
                        </div>
                    </div>
                }
            }
        </div>
    `,
    styles: `
        .custom-schedule {
            --bs-border-color: rgba(var(--bs-primary-rgb), 0.15);
        }

        .interval-input {
            display: block;
            width: 5.5rem;
        }

        .unit-select {
            width: 11rem;
        }

        .schedule-summary {
            background-color: var(--bs-tertiary-bg);
            border-left: 3px solid var(--bs-primary);
        }

        .next-deliveries th:first-child {
            width: 40%;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomScheduleComponent {
    readonly schedule = input.required<ExportScheduleForm>();
    readonly daysBackInTime = input<number | null>(null);
    readonly latestKnownReadings = input(false);

    private readonly translate = inject(TRANSLATE);
    private readonly translateService = inject(TranslateService);
    private readonly featureFlagService = inject(FeatureFlagService);

    private readonly endedMonthsEnabled = toSignal(
        this.featureFlagService.isFeatureFlagEnabled$('agi-19486-ended-months-schedule'),
        {initialValue: false},
    );

    protected readonly value = scheduleFormValue(this.schedule);

    protected readonly unit = computed(() => this.value()?.scheduleUnit ?? 'DAYS');

    protected readonly periodOffered = computed(
        () => this.endedMonthsEnabled() && this.unit() === 'MONTHS',
    );

    protected readonly period = computed(() => this.value()?.schedulePeriod ?? 'DAYS_BACK');

    protected readonly dayOfMonthShown = computed(
        () => this.unit() === 'MONTHS' && this.period() === 'DAYS_BACK',
    );

    protected readonly periodOptions = computed<ReadonlyArray<OptionItem>>(() => {
        const interval = this.value()?.scheduleInterval ?? 1;
        const months = Number.isInteger(interval) && interval > 1 ? `${interval}` : null;
        const endedMonths: OptionItem = {
            id: 'ENDED_MONTHS',
            titleKey:
                months === null
                    ? 'savedexport_custom_schedule_period_ended_month_title'
                    : 'savedexport_custom_schedule_period_ended_months_title',
            descriptionKey:
                months === null
                    ? 'savedexport_custom_schedule_period_ended_month_desc'
                    : 'savedexport_custom_schedule_period_ended_months_desc',
            ...(months === null ? {} : {titleParams: [months], descriptionParams: [months]}),
        };
        return [endedMonths, this.latestKnownReadings() ? setDayOption : daysBackOption];
    });

    protected readonly sentOnHeader = computed(() =>
        this.period() === 'ENDED_MONTHS'
            ? this.translate('savedexport_custom_schedule_next_sent_first', 'online', 'export')
            : this.translate('savedexport_custom_schedule_next_sent_day', 'online', 'export', [
                  `${this.value()?.scheduleDayOfMonth}`,
              ]),
    );

    protected readonly nextDeliveries = computed(() => {
        const current = this.value();
        if (!this.periodOffered() || current?.startDate == null || !current.startDate.isValid) {
            return [];
        }
        const period = this.period();
        const daysBackInTime = this.daysBackInTime();
        const intervalValid =
            Number.isInteger(current.scheduleInterval) && current.scheduleInterval >= 1;
        const dayOfMonthValid =
            period === 'ENDED_MONTHS' ||
            (Number.isInteger(current.scheduleDayOfMonth) &&
                current.scheduleDayOfMonth >= 1 &&
                current.scheduleDayOfMonth <= maxScheduleDayOfMonth);
        const daysBackValid = daysBackInTime === null || Number.isInteger(daysBackInTime);
        if (!intervalValid || !dayOfMonthValid || !daysBackValid) {
            return [];
        }
        const language = this.translateService.language;
        return nextMonthlyDeliveries(
            {
                startDate: current.startDate,
                interval: current.scheduleInterval,
                dayOfMonth: current.scheduleDayOfMonth,
                period,
                daysBackInTime: this.daysBackInTime(),
            },
            DateTime.utc(),
        ).map(({sentOn, holdsFrom, holdsTo}) => ({
            sentOn: formatDate(sentOn, language, sentOnFormat),
            holds:
                holdsFrom === null
                    ? formatDate(holdsTo, language, holdsFormat)
                    : this.translate('savedexport_custom_schedule_next_range', 'online', 'export', [
                          formatDate(holdsFrom, language, holdsFormat),
                          formatDate(holdsTo, language, holdsFormat),
                      ]),
        }));
    });

    protected readonly selectedWeekdays = computed<ReadonlyArray<SavedExportWeekday>>(
        () => this.value()?.scheduleWeekdays ?? [],
    );

    protected readonly summaryComplete = computed(() => {
        const current = this.value();
        return (
            Number.isFinite(current?.scheduleInterval) &&
            (this.unit() !== 'MONTHS' || Number.isFinite(current?.scheduleDayOfMonth)) &&
            (this.unit() !== 'WEEKS' || this.selectedWeekdays().length > 0)
        );
    });

    protected readonly summaryKey = computed(() => {
        const single = this.value()?.scheduleInterval === 1;
        return `savedexport_custom_schedule_summary_${this.unit().toLowerCase()}_${single ? 'one' : 'many'}`;
    });

    protected readonly summaryArguments = computed(() => {
        const current = this.value();
        if (current === undefined) {
            return [];
        }
        const detail =
            this.unit() === 'WEEKS'
                ? current.scheduleWeekdays
                      .map(weekday => this.translate(weekdayLabelKeys[weekday], 'online', 'export'))
                      .join(', ')
                : `${current.scheduleDayOfMonth}`;
        const carriesDetail = this.unit() === 'WEEKS' || this.unit() === 'MONTHS';
        const interval = current.scheduleInterval === 1 ? [] : [`${current.scheduleInterval}`];
        return carriesDetail ? [...interval, detail] : interval;
    });

    protected readonly unitOptions = computed(
        () =>
            new Map(
                savedExportScheduleUnits.map(unit => [
                    unit,
                    this.translate(scheduleUnitLabelKeys[unit], 'online', 'export'),
                ]),
            ),
    );
    protected readonly weekdayOptions = weekdayOptions;

    protected selectPeriod(id: string): void {
        const control = this.schedule().controls.schedulePeriod;
        control.setValue(savedExportSchedulePeriodSchema.parse(id));
        control.markAsDirty();
    }

    protected toggleWeekday(weekday: SavedExportWeekday): void {
        const control = this.schedule().controls.scheduleWeekdays;
        const selected = control.value.includes(weekday)
            ? control.value.filter(entry => entry !== weekday)
            : savedExportWeekdays.filter(
                  entry => entry === weekday || control.value.includes(entry),
              );
        control.setValue([...selected]);
        control.markAsDirty();
    }
}
